import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { base44 } from "@/api/base44Client";
import LangThemeToggle from "@/components/LangThemeToggle";
import OrderNotifier from "@/components/admin/OrderNotifier";
import { useApp } from "@/lib/AppContext";
import { t } from "@/lib/translations";
import { clearAdminAuthCache } from "@/components/admin/AdminGuard";

// Bottom nav items for mobile (max 5)
const getMobileNav = (isAr) => [
  { path: "/admin/dashboard", icon: "📊", label: isAr ? "الرئيسية" : "Home" },
  { path: "/admin/laundry/orders", icon: "📦", label: isAr ? "الطلبات" : "Orders" },
  { path: "/admin/laundry/delegates", icon: "👤", label: isAr ? "المندوبون" : "Delegates" },
  { path: "/admin/offers", icon: "🎁", label: isAr ? "العروض" : "Offers" },
  { path: "/admin/more", icon: "☰", label: isAr ? "المزيد" : "More" },
];

const getNavItems = (tr, isAr) => [
  { path: "/admin/dashboard", icon: "📊", label: tr.dashboard, group: null },
  { group: "laundry", groupLabel: isAr ? "🧺 المغسلة الإلكترونية" : "🧺 E-Laundry" },
  { path: "/admin/laundry/orders", icon: "📦", label: isAr ? "الطلبات" : "Orders", group: "laundry" },
  { path: "/admin/laundry/delegates", icon: "👤", label: isAr ? "المندوبون" : "Delegates", group: "laundry" },
  { path: "/admin/laundry/services", icon: "🧾", label: isAr ? "الخدمات والأسعار" : "Services & Pricing", group: "laundry" },
  { group: "wheel", groupLabel: isAr ? "🎡 عجلة العروض" : "🎡 Offers Wheel" },
  { path: "/admin/offers", icon: "🎁", label: tr.offers, group: "wheel" },
  { path: "/admin/coupons", icon: "🎫", label: tr.coupons, group: "wheel" },
  { path: "/admin/verify", icon: "✅", label: tr.verify, group: "wheel" },
  { path: "/admin/customers", icon: "👥", label: tr.customers, group: "wheel" },
  { group: "settings", groupLabel: isAr ? "⚙️ إعدادات" : "⚙️ Settings" },
  { path: "/admin/settings", icon: "⚙️", label: tr.settings, group: "settings" },
  { path: "/admin/import", icon: "📥", label: isAr ? "استيراد عملاء" : "Import Customers", group: "settings" },
  { group: "landing", groupLabel: isAr ? "🌐 اللاندينج بيج" : "🌐 Landing Page" },
  { path: "/admin/reviews", icon: "⭐", label: isAr ? "التقييمات" : "Reviews", group: "landing" },
  { path: "/admin/promos", icon: "🖼️", label: isAr ? "البانرات الترويجية" : "Promo Banners", group: "landing" },
  { path: "/admin/offer-banners", icon: "🏷️", label: isAr ? "بنرات العروض" : "Offer Banners", group: "landing" },

];

// Collapsible sidebar section
function SidebarSection({ groupLabel, group, isAr, isCollapsed, onToggle, children }) {
  return (
    <div className="mb-1">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-4 py-2.5 rounded-xl bg-sidebar-accent/40 hover:bg-sidebar-accent/60 transition-colors group mb-2"
      >
        <span className="text-sm font-black text-sidebar-foreground flex-1 text-right">{groupLabel}</span>
        <svg
          className={`w-4 h-4 text-sidebar-foreground transition-transform duration-200 flex-shrink-0 ${isCollapsed ? (isAr ? 'rotate-90' : '-rotate-90') : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isAr ? "M9 5l7 7-7 7" : "M19 9l-7 7-7-7"} />
        </svg>
      </button>
      {!isCollapsed && <div className="space-y-0.5">{children}</div>}
    </div>
  );
}

// Drawer menu for "المزيد" on mobile
function MobileDrawer({ open, onClose, navItems, location, tr, lang, onLogout }) {
  const isAr = lang === "ar";
  const [collapsedGroups, setCollapsedGroups] = useState({});
  
  const toggleGroup = (groupName) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };
  
  return (
    <>
      {open && <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={onClose} />}
      <div className={`fixed bottom-0 left-0 right-0 z-50 lg:hidden bg-background rounded-t-3xl shadow-2xl transition-transform duration-300 ${open ? "translate-y-0" : "translate-y-full"}`}
        style={{ maxHeight: "80vh" }}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <p className="font-black text-foreground">{isAr ? "القائمة" : "Menu"}</p>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary text-muted-foreground text-lg">✕</button>
        </div>
        <div className="overflow-y-auto px-4 py-3 pb-8 space-y-0.5" style={{ maxHeight: "65vh" }}>
          {navItems.map((item, idx) => {
            // Group header with collapsible functionality
            if (item.groupLabel && !item.path) {
              const isCollapsed = collapsedGroups[item.group];
              return (
                <div key={`g-${idx}`} className="pt-3 pb-1">
                  <button
                    onClick={() => toggleGroup(item.group)}
                    className="w-full flex items-center gap-2 px-4 py-2.5 rounded-xl bg-secondary hover:bg-secondary/80 transition-colors group mb-2"
                  >
                    <span className="text-sm font-black text-foreground flex-1 text-right">{item.groupLabel}</span>
                    <svg 
                      className={`w-4 h-4 text-foreground transition-transform duration-200 flex-shrink-0 ${isCollapsed ? (isAr ? 'rotate-90' : '-rotate-90') : ''}`}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isAr ? "M9 5l7 7-7 7" : "M19 9l-7 7-7-7"} />
                    </svg>
                  </button>
                </div>
              );
            }
            
            // Skip items if their group is collapsed
            if (item.group && collapsedGroups[item.group]) {
              return null;
            }
            
            const isActive = location.pathname === item.path;
            return (
              <Link 
                key={item.path} 
                to={item.path} 
                onClick={onClose}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all 
                  ${isActive ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-secondary"}
                  ${item.group ? "ml-4 rtl:mr-4" : ""}
                `}
              >
                <span className="text-lg w-7 text-center">{item.icon}</span>
                {item.label}
              </Link>
            );
          })}
          <div className="pt-3 border-t border-border mt-2">
            <Link to="/" target="_blank" onClick={onClose}
              className="flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-semibold text-foreground hover:bg-secondary">
              <span className="text-lg w-7 text-center">🎡</span> {lang === "ar" ? "صفحة عجلة العروض" : "Offers Wheel Page"}
            </Link>
            <Link to="/laundry" target="_blank" onClick={onClose}
              className="flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-semibold text-foreground hover:bg-secondary">
              <span className="text-lg w-7 text-center">🧺</span> {lang === "ar" ? "صفحة المغسلة" : "Laundry Page"}
            </Link>
          </div>
          <div className="px-1 py-2">
            <LangThemeToggle />
          </div>
          <button onClick={onLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-semibold text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20 transition-all">
            <span className="text-lg w-7 text-center">🚪</span> {tr.logout}
          </button>
        </div>
      </div>
    </>
  );
}

export default function AdminLayout({ children }) {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const { lang } = useApp();
  const tr = t[lang];
  const isAr = lang === "ar";
  const navItems = getNavItems(tr, isAr);
  const mobileNav = getMobileNav(isAr);

  const toggleGroup = (groupName) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };

  const handleLogout = async () => {
    sessionStorage.removeItem("admin_session");
    clearAdminAuthCache();
    await base44.auth.logout("/");
  };

  const currentLabel = navItems.find(n => n.path === location.pathname)?.label || tr.dashboard;

  return (
    <div className="min-h-screen bg-background flex" dir={isAr ? "rtl" : "ltr"}>
      {/* ── Desktop Sidebar ── */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-20 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}
      <aside className={`
        fixed top-0 right-0 h-full w-64 bg-sidebar z-30 flex flex-col transition-transform duration-300
        ${sidebarOpen ? "translate-x-0" : "translate-x-full lg:translate-x-0"}
        lg:static lg:translate-x-0
      `}>
        <div className="p-5 border-b border-sidebar-border flex items-center gap-3 flex-shrink-0">
          <img src="https://media.base44.com/images/public/user_69729b1c2410c5c031f1d29a/291b90bb1_.png" alt="logo" className="w-10 h-10 object-contain" />
          <div>
            <p className="text-sidebar-foreground font-black text-sm">{tr.appName}</p>
            <p className="text-sidebar-foreground/50 text-xs">{tr.dashboard}</p>
          </div>
        </div>
        <nav className="flex-1 p-4 space-y-0.5 overflow-y-auto scrollbar-hide pb-20 lg:pb-4">
          {(() => {
            const sections = [];
            let currentGroup = null;
            let currentGroupItems = [];
            
            navItems.forEach((item, idx) => {
              if (item.groupLabel && !item.path) {
                if (currentGroup !== null) {
                  sections.push({
                    type: 'group',
                    group: currentGroup,
                    groupLabel: navItems.find(i => i.group === currentGroup)?.groupLabel,
                    items: currentGroupItems
                  });
                }
                currentGroup = item.group;
                currentGroupItems = [];
              } else if (item.group) {
                currentGroupItems.push(item);
              } else {
                sections.push({ type: 'item', item });
              }
            });
            
            if (currentGroup !== null) {
              sections.push({
                type: 'group',
                group: currentGroup,
                groupLabel: navItems.find(i => i.group === currentGroup)?.groupLabel,
                items: currentGroupItems
              });
            }
            
            return sections.map((section, idx) => {
              if (section.type === 'item') {
                const isActive = location.pathname === section.item.path;
                return (
                  <Link 
                    key={section.item.path} 
                    to={section.item.path} 
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-4 py-2.5 rounded-xl font-medium text-sm transition-all
                      ${isActive ? "bg-sidebar-primary text-sidebar-primary-foreground shadow-md" : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"}`}
                  >
                    <span className="text-base">{section.item.icon}</span>
                    {section.item.label}
                  </Link>
                );
              } else {
                const isCollapsed = collapsedGroups[section.group];
                return (
                  <SidebarSection 
                    key={`group-${idx}`}
                    groupLabel={section.groupLabel}
                    group={section.group}
                    isAr={isAr}
                    isCollapsed={isCollapsed}
                    onToggle={() => toggleGroup(section.group)}
                  >
                    {section.items.map(item => {
                      const isActive = location.pathname === item.path;
                      return (
                        <Link 
                          key={item.path} 
                          to={item.path} 
                          onClick={() => setSidebarOpen(false)}
                          className={`flex items-center gap-3 px-4 py-2.5 rounded-xl font-medium text-sm transition-all
                            ${isActive ? "bg-sidebar-primary text-sidebar-primary-foreground shadow-md" : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"}`}
                        >
                          <span className="text-base">{item.icon}</span>
                          {item.label}
                        </Link>
                      );
                    })}
                  </SidebarSection>
                );
              }
            });
          })()}
        </nav>
        <div className="p-4 border-t border-sidebar-border space-y-1 flex-shrink-0">
          <div className="px-1 py-1"><LangThemeToggle variant="sidebar" /></div>
          <Link to="/landing" target="_blank" className="flex items-center gap-2 px-4 py-2 text-sidebar-foreground/70 hover:text-sidebar-foreground text-sm rounded-lg hover:bg-sidebar-accent transition-colors">
            <span>🌐</span> {isAr ? "اللاندينج بيج" : "Landing Page"}
          </Link>
          <Link to="/" target="_blank" className="flex items-center gap-2 px-4 py-2 text-sidebar-foreground/70 hover:text-sidebar-foreground text-sm rounded-lg hover:bg-sidebar-accent transition-colors">
            <span>🎡</span> {tr.customerPage}
          </Link>
          <Link to="/laundry" target="_blank" className="flex items-center gap-2 px-4 py-2 text-sidebar-foreground/70 hover:text-sidebar-foreground text-sm rounded-lg hover:bg-sidebar-accent transition-colors">
            <span>🧺</span> {isAr ? "صفحة المغسلة" : "Laundry Page"}
          </Link>
          <button onClick={handleLogout} className="w-full flex items-center gap-2 px-4 py-2 text-red-400 hover:text-red-300 text-sm rounded-lg hover:bg-red-500/10 transition-colors">
            <span>🚪</span> {tr.logout}
          </button>
        </div>
      </aside>

      {/* ── Main Content ── */}
      <div className="flex-1 flex flex-col min-w-0 lg:mr-0 pb-20 lg:pb-0">
        {/* Top Bar */}
        <header className="bg-background/95 backdrop-blur-md border-b border-border px-4 py-3 flex items-center justify-between sticky top-0 z-10">
          {/* Mobile: hamburger */}
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 -ml-1 rounded-xl hover:bg-secondary transition-colors">
            <svg className="w-6 h-6 text-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          {/* Page title — center on mobile */}
          <h2 className="font-black text-foreground text-base absolute left-1/2 -translate-x-1/2 lg:static lg:translate-x-0">
            {currentLabel}
          </h2>

          {/* Right: notifier + avatar */}
          <div className="flex items-center gap-2">
            <div className="hidden lg:block"><LangThemeToggle /></div>
            <OrderNotifier />
            <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white text-sm font-bold">
              {lang === "ar" ? "م" : "A"}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto">
          <div className="p-4 md:p-6">
            {children}
          </div>
        </main>
      </div>

      {/* ── Mobile Bottom Navigation ── */}
      <nav className="fixed bottom-0 left-0 right-0 z-30 lg:hidden bg-background/95 backdrop-blur-md border-t border-border safe-bottom">
        <div className="flex items-center justify-around px-2 pt-2 pb-2">
          {mobileNav.map(item => {
            const isMore = item.path === "/admin/more";
            const isActive = isMore ? drawerOpen : location.pathname === item.path;
            return (
              <button
                key={item.path}
                onClick={() => {
                  if (isMore) { setDrawerOpen(true); }
                }}
                className="flex-1 flex flex-col items-center gap-0.5 py-1 transition-all"
              >
                {isMore ? (
                  <button onClick={() => setDrawerOpen(true)} className="flex flex-col items-center gap-0.5 w-full">
                    <span className={`text-xl leading-none transition-all ${isActive ? "tab-active scale-110" : ""}`}>{item.icon}</span>
                    <span className={`text-[10px] font-semibold transition-all ${isActive ? "text-primary font-black" : "text-muted-foreground"}`}>{item.label}</span>
                  </button>
                ) : (
                  <Link to={item.path} className="flex flex-col items-center gap-0.5 w-full">
                    <span className={`text-xl leading-none transition-all ${isActive ? "tab-active scale-110" : ""}`}>{item.icon}</span>
                    <span className={`text-[10px] font-semibold transition-all ${isActive ? "text-primary font-black" : "text-muted-foreground"}`}>{item.label}</span>
                    {isActive && <span className="w-1 h-1 bg-primary rounded-full" />}
                  </Link>
                )}
              </button>
            );
          })}
        </div>
      </nav>

      {/* Mobile Drawer */}
      <MobileDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        navItems={navItems}
        location={location}
        tr={tr}
        lang={lang}
        onLogout={handleLogout}
      />
    </div>
  );
}